import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Layers } from 'lucide-react';
import { PublicBundle } from '../services/publicService';
import { formatPrice } from '../lib/format';
import { compareBundlePrice } from '../lib/bundlePricing';
import { THUMB_PLACEHOLDER, onThumbError } from './ui/imageFallback';
import { thumbnailSrc } from '../lib/imageUrl';

interface PublicBundleCardProps {
  bundle: PublicBundle;
  /** Tenants without `sell_courses` give everything away, whatever price is stored. */
  canSell: boolean;
}

/** Access-duration copy: "1 year access", "90 days access". */
function accessLabel(days?: number): string {
  if (!days) return 'Lifetime access';
  if (days >= 365) {
    const years = Math.round(days / 365);
    return `${years} year${years > 1 ? 's' : ''} access`;
  }
  return `${days} days access`;
}

/**
 * A bundle on the marketing catalogue, for visitors who aren't signed in.
 *
 * Deliberately shows the member courses by name: the shelf rule takes those
 * courses off the grid below, so this card is now the only place a visitor can
 * discover they exist.
 */
export const PublicBundleCard: React.FC<PublicBundleCardProps> = ({ bundle, canSell }) => {
  const priceComparison = compareBundlePrice(
    bundle.price,
    bundle.courses.map((c) => c.price),
  );
  // `bundle.price > 0` matters: on a free pack "Free" is already the strongest
  // thing the price can say, and a "Save R100" chip beside it reads as clutter.
  // BundleCard reaches the same result by nesting the chip inside its paid
  // branch — stated explicitly here so the two cards can't drift apart.
  const showSaving = canSell && bundle.price > 0 && priceComparison.verdict === 'saving';
  const thumbnail = thumbnailSrc(bundle.thumbnailUrl || bundle.courses[0]?.thumbnailUrl) || THUMB_PLACEHOLDER;

  return (
    <Link to={`/bundle/${bundle.id}`} className="group h-full">
      <div className="bg-paper-dark rounded-2xl overflow-hidden hover:shadow-card-hover shadow-card transition-all duration-300 flex flex-col h-full transform hover:-translate-y-1 ring-1 ring-paper-dark">
        <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
          <img
            src={thumbnail}
            onError={onThumbError}
            alt={bundle.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />

          <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
            <span className="bg-white/95 backdrop-blur px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-gray-900 shadow-sm">
              {canSell && bundle.price > 0 ? formatPrice(bundle.price) : 'Free'}
            </span>
            {showSaving && (
              <span className="bg-green-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                Save {formatPrice(priceComparison.saving)}
              </span>
            )}
          </div>

          <div className="absolute bottom-3 left-3 text-white font-medium text-xs flex items-center gap-2">
            <span className="bg-white/95 backdrop-blur px-2.5 py-1 rounded-md text-gray-900 text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5">
              <Layers className="h-3 w-3" /> Bundle
            </span>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-1">
          <h3 className="text-xl font-bold text-paper-dark-ink mb-2 leading-snug group-hover:text-primary-300 transition-colors tracking-tight">
            {bundle.title}
          </h3>
          {bundle.description && (
            <p className="text-sm text-paper-dark-muted mb-4 line-clamp-2 leading-relaxed">
              {bundle.description}
            </p>
          )}

          {bundle.courses.length > 0 && (
            <ul className="mb-4 flex flex-col gap-1.5 flex-1">
              {bundle.courses.slice(0, 4).map((course) => (
                <li
                  key={course.id}
                  className="text-sm text-paper-dark-ink flex items-start gap-2 leading-snug"
                >
                  <ArrowRight
                    aria-hidden
                    className="h-3.5 w-3.5 mt-0.5 text-primary-400 flex-shrink-0"
                  />
                  <span className="line-clamp-1">{course.title}</span>
                </li>
              ))}
              {bundle.courses.length > 4 && (
                <li className="text-sm text-paper-dark-muted font-medium pl-[1.375rem]">
                  + {bundle.courses.length - 4} more
                </li>
              )}
            </ul>
          )}

          <div className="flex items-center gap-4 text-xs font-semibold text-paper-dark-muted border-t border-paper-dark-line pt-5 mt-auto">
            <div className="flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              <span>
                {bundle.courses.length} {bundle.courses.length === 1 ? 'course' : 'courses'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>{accessLabel(bundle.accessDurationDays)}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
